import { useState } from "react";
import { useVoice } from "@/contexts/VoiceContext";
import { VOICE_CATEGORIES } from "@/contexts/VoiceContext";
import { Volume2, Check, ChevronDown, ChevronRight, X, Globe, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const GENDER_ICONS = {
  Female: "text-pink-400",
  Male: "text-blue-400",
  Neutral: "text-amber-400",
};

export function VoiceSelector({ open, onClose }) {
  const { voice, setVoice, speakText, speaking, stopSpeaking } = useVoice();
  const [expandedCat, setExpandedCat] = useState(null);
  const [previewingVoice, setPreviewingVoice] = useState(null);

  const previewVoice = async (voiceKey) => {
    if (speaking) stopSpeaking();
    setPreviewingVoice(voiceKey);
    // Temporarily set voice to preview, then restore
    const prev = voice;
    setVoice(voiceKey);
    await speakText("Hello, I'm your Omni companion. How can I help you today?", `preview-${voiceKey}`);
    setPreviewingVoice(null);
    // Don't restore - user is previewing, let them choose
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-card border border-border/50 rounded-3xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-3 border-b border-border/30">
          <div>
            <h3 className="text-lg font-heading font-semibold">Omni's Voice</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Choose how Omni sounds when speaking to you</p>
          </div>
          <button
            data-testid="voice-selector-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Voice Categories */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)] space-y-2">
          {VOICE_CATEGORIES.map(cat => {
            const isExpanded = expandedCat === cat.id || cat.voices.some(v => v.key === voice);
            const hasSelected = cat.voices.some(v => v.key === voice);

            return (
              <div key={cat.id} className="rounded-2xl border border-border/30 overflow-hidden">
                {/* Category Header */}
                <button
                  data-testid={`voice-cat-${cat.id}`}
                  onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-4 py-3 text-left transition-colors",
                    hasSelected ? "bg-primary/5" : "hover:bg-muted/30"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{cat.label}</span>
                      {hasSelected && (
                        <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">Active</span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">{cat.desc}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  )}
                </button>

                {/* Voices List */}
                {isExpanded && (
                  <div className="px-3 pb-3 space-y-1.5">
                    {cat.voices.map(v => {
                      const isSelected = voice === v.key;
                      const isPreviewing = previewingVoice === v.key;

                      return (
                        <div
                          key={v.key}
                          data-testid={`voice-option-${v.key}`}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer",
                            isSelected
                              ? "bg-primary/10 border border-primary/20"
                              : "hover:bg-muted/40 border border-transparent"
                          )}
                          onClick={() => setVoice(v.key)}
                        >
                          {/* Voice Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={cn("text-sm font-medium", isSelected && "text-primary")}>{v.label}</span>
                              <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full bg-muted/60", GENDER_ICONS[v.gender])}>
                                {v.gender}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-[11px] text-muted-foreground">{v.desc}</span>
                              <span className="text-[10px] text-muted-foreground/60 flex items-center gap-0.5">
                                <Globe className="w-2.5 h-2.5" />
                                {v.origin}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              data-testid={`voice-preview-${v.key}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                previewVoice(v.key);
                              }}
                              className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center transition-all",
                                isPreviewing
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted/50 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                              )}
                              title="Preview this voice"
                            >
                              {isPreviewing ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Volume2 className="w-3.5 h-3.5" />
                              )}
                            </button>
                            {isSelected && (
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-primary-foreground" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
